import { Assets, Texture } from 'pixi.js';
import { COUNTRIES, countryByCode } from '@fbwl/shared';

const FLAG_W = 320;
const FLAG_H = 240;

/**
 * Supplies a texture for every nation.
 *
 * Real flags are 4:3 SVGs copied out of `flag-icons` at install time and served
 * from `/flags/<code>.svg`. They are loaded lazily and in the background.
 *
 * Until a flag arrives - and permanently, if the asset step was skipped - a
 * procedurally drawn card stands in, built from the nation's two flag colours.
 * That fallback is the reason the broadcast can never show an empty rectangle:
 * a missing asset degrades to a plausible graphic instead of a hole in the
 * middle of a live final.
 */
export class FlagTextureFactory {
  private readonly ready = new Map<string, Texture>();
  private readonly fallbacks = new Map<string, Texture>();
  private readonly inflight = new Map<string, Promise<Texture>>();
  private failures = 0;

  /** Immediately usable texture. Real flag if loaded, generated card if not. */
  get(code: string): Texture {
    return this.ready.get(code) ?? this.fallback(code);
  }

  has(code: string): boolean {
    return this.ready.has(code);
  }

  get failureCount(): number {
    return this.failures;
  }

  async load(code: string): Promise<Texture> {
    const cached = this.ready.get(code);
    if (cached) return cached;

    const existing = this.inflight.get(code);
    if (existing) return existing;

    const promise = Assets.load<Texture>({
      src: `flags/${code.toLowerCase()}.svg`,
      data: { resolution: 2, parseAsGraphicsContext: false },
    })
      .then((texture) => {
        this.ready.set(code, texture);
        return texture;
      })
      .catch(() => {
        this.failures += 1;
        return this.fallback(code);
      })
      .finally(() => {
        this.inflight.delete(code);
      });

    this.inflight.set(code, promise);
    return promise;
  }

  /**
   * Warm the cache without blocking the first frame.
   *
   * Concurrency is capped: firing 194 SVG decodes at once on a modest VPS-hosted
   * browser source stalls the compositor for whole seconds.
   */
  async preload(
    codes: readonly string[] = COUNTRIES.map((c) => c.code),
    onProgress?: (loaded: number, total: number) => void,
    concurrency = 12,
  ): Promise<void> {
    const queue = [...codes];
    const total = queue.length;
    let done = 0;

    const worker = async (): Promise<void> => {
      for (;;) {
        const code = queue.shift();
        if (!code) return;
        await this.load(code);
        done += 1;
        onProgress?.(done, total);
      }
    };

    await Promise.all(Array.from({ length: Math.min(concurrency, total) }, worker));
  }

  /** Generated stand-in card. Cached, so it is drawn once per nation at most. */
  fallback(code: string): Texture {
    const cached = this.fallbacks.get(code);
    if (cached) return cached;

    const country = countryByCode(code);
    const primary = country?.primaryColor ?? '#1b2740';
    const accent = country?.accentColor ?? '#38bdf8';

    const canvas = document.createElement('canvas');
    canvas.width = FLAG_W;
    canvas.height = FLAG_H;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      const third = FLAG_W / 3;
      ctx.fillStyle = primary;
      ctx.fillRect(0, 0, third, FLAG_H);
      ctx.fillStyle = '#f4f7fb';
      ctx.fillRect(third, 0, third, FLAG_H);
      ctx.fillStyle = accent;
      ctx.fillRect(third * 2, 0, third, FLAG_H);

      // A soft vertical shade stops the flat fill reading as a broken image.
      const shade = ctx.createLinearGradient(0, 0, 0, FLAG_H);
      shade.addColorStop(0, 'rgba(255,255,255,0.16)');
      shade.addColorStop(0.55, 'rgba(0,0,0,0)');
      shade.addColorStop(1, 'rgba(0,0,0,0.28)');
      ctx.fillStyle = shade;
      ctx.fillRect(0, 0, FLAG_W, FLAG_H);

      ctx.fillStyle = 'rgba(6,10,20,0.72)';
      roundRect(ctx, FLAG_W / 2 - 68, FLAG_H / 2 - 34, 136, 68, 14);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.font = '700 54px Bahnschrift, "Segoe UI", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(code.toUpperCase(), FLAG_W / 2, FLAG_H / 2 + 3);
    }

    const texture = Texture.from(canvas);
    this.fallbacks.set(code, texture);
    return texture;
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Shared instance: textures are global, and one cache beats six. */
export const flagTextures = new FlagTextureFactory();
